import commaNumber from 'comma-number';
import type { KeyboardEvent } from 'react';

import { Flex, VFlex } from '@/components/base';
import { Span } from '@/components/Typography/Typo';
import type {
  CompositedSignalIssue,
  SIGNAL_DATE_PRESET,
} from '@/features/(signal)/signals/model';
import { ChangesLabel } from '@/features/(signal)/signals/ui/changes-label';
import { SignalBadge } from '@/features/(signal)/signals/ui/signal-badge';

type Props = {
  issue: CompositedSignalIssue;
  rank: number;
  datePreset: SIGNAL_DATE_PRESET;
  onClick: (
    issueName: string,
    severity: CompositedSignalIssue['severity'],
  ) => void;
};

const HomeTopSignalRow = ({ issue, rank, datePreset, onClick }: Props) => {
  const firstSignal = issue.signalTitles[0];
  const onRowClick = () => onClick(issue.name, issue.severity);
  const onRowKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onRowClick();
    }
  };

  return (
    <Flex
      className="bg-gray-100 hover:bg-gray-200 rounded-xl px-3 py-2 border border-transparent hover:border-primary-100 transition-colors cursor-pointer"
      j="space-between"
      a="center"
      role="button"
      tabIndex={0}
      onClick={onRowClick}
      onKeyDown={onRowKeyDown}
    >
      <Flex g={4} style={{ minWidth: 0 }}>
        <Span type="label1" className="text-gray-700" style={{ minWidth: 24 }}>
          {rank}
        </Span>
        <VFlex g={4} style={{ minWidth: 0 }}>
          <Span
            type="label2"
            weight="m"
            className="text-gray-900 break-all line-clamp-2"
          >
            {issue.name}
          </Span>
          <Flex g={8} a="center" className="flex-wrap">
            <Span type="label3" className="text-gray-600">
              Feedback count {commaNumber(issue.selectedPeriodCount)}
            </Span>
            <ChangesLabel
              className="text-xs leading-5"
              comparisonPeriodCount={issue.comparisonPeriodCount}
              selectedPeriodCount={issue.selectedPeriodCount}
              datePreset={datePreset}
            />
            {firstSignal && (
              <SignalBadge
                title={firstSignal}
                tooltip={issue.signalTitles.join(', ')}
              />
            )}
          </Flex>
        </VFlex>
      </Flex>
    </Flex>
  );
};

export default HomeTopSignalRow;
